import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, CreditCard, ArrowRight, LogIn, UserPlus, Mail, ShieldCheck } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import Seo from '@/components/Seo';
import { BRAND, money, planLabel, PLAN_INCLUDED_LEADS, PLAN_PRICES } from '@/data/constants';
import { claimActivation, lookupReturnState, setWelcomeTicket, tourSeen } from '@/lib/activation';
import { track } from '@/lib/tracking';

/**
 * CHECKOUT RETURN PAGE  —  /payment-complete
 *
 * The customer must never be left on the payment provider's receipt with no
 * instructions, and must never have to "claim" anything by hand. This page:
 *
 *   1. Waits for the SIGNED webhook (never trusts the browser for credits) and
 *      shows "Payment received. We are activating your credits…".
 *   2. Calls mkt_claim_payment_activation() — a database function that attaches
 *      the verified payment to THIS account and assigns the plan's credits
 *      exactly once (Basic 5 · Pro 10 · Premium 20 · single lead 1).
 *   3. Sends them straight to Projects, where the balance is visible and the
 *      credits can be spent, with a confirmation banner and the 5-step tour.
 *
 * Account states handled, so nobody ends up stuck:
 *   signed in      -> activate + continue automatically
 *   signed out     -> big Sign in button that returns HERE (?next=) and resumes
 *   no account yet -> Create account button, prefilled with the paid email,
 *                     that returns here and resumes
 */

const POLL_MS = 3000;
const MAX_WAIT_MS = 90000;

const PaymentComplete: React.FC = () => {
  const { t, lang, path } = useI18n();
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const planParam = (params.get('plan') || '').toLowerCase();
  const emailParam = (params.get('email') || '').trim().toLowerCase();

  const [phase, setPhase] = useState<'waiting' | 'activated' | 'signin' | 'signup' | 'help'>('waiting');
  const [plan, setPlan] = useState(PLAN_INCLUDED_LEADS[planParam] ? planParam : '');
  const [credits, setCredits] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const done = useRef(false);
  const started = useRef(Date.now());

  /** Hand off to Projects with the confirmation banner + first-buyer tour. */
  const goToProjects = useCallback(
    (activePlan: string, availableCredits: number) => {
      if (done.current) return;
      done.current = true;
      setWelcomeTicket({
        plan: activePlan,
        credits: availableCredits,
        kind: activePlan ? 'plan' : 'lead',
      });
      track('cta_click', { action: 'post_payment_to_projects', plan: activePlan || 'lead' });
      setPhase('activated');
      setPlan(activePlan);
      setCredits(availableCredits);
      // Short, visible confirmation, then straight to where credits are spent.
      window.setTimeout(() => navigate(path('/jobs'), { replace: true }), 1600);
    },
    [navigate, path]
  );

  /** One activation attempt. Returns true when the plan is live. */
  const attempt = useCallback(async (): Promise<boolean> => {
    if (user) {
      const res = await claimActivation();
      if (res.state === 'activated') {
        goToProjects(res.plan, res.credits);
        return true;
      }
      // A signed-in buyer with only $29 extra credits and no plan still gets
      // moved along — they have something to spend.
      if (res.state === 'awaiting_webhook' && res.credits > 0) {
        goToProjects('', res.credits);
        return true;
      }
      return false;
    }

    // Signed out: find out whether the webhook landed and whether the paying
    // email already has an account, then ask them to sign in / sign up.
    const email = emailParam || '';
    if (!email) {
      setPhase('signin');
      return false;
    }
    const state = await lookupReturnState(email, planParam);
    if (state.plan) setPlan(state.plan);
    if (state.webhookReceived || state.activated) {
      setPhase(state.hasAccount ? 'signin' : 'signup');
      return true; // stop polling — the next move is theirs
    }
    return false;
  }, [user, emailParam, planParam, goToProjects]);

  useEffect(() => {
    if (loading || done.current) return;
    let cancelled = false;
    let timer = 0;

    const run = async () => {
      const finished = await attempt();
      if (cancelled || finished) return;
      const waited = Date.now() - started.current;
      setElapsed(waited);
      if (waited > MAX_WAIT_MS) {
        setPhase('help');
        return;
      }
      timer = window.setTimeout(run, POLL_MS);
    };

    run();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [attempt, loading]);

  const planName = plan ? planLabel(plan, lang) : '';
  const planCredits = plan ? PLAN_INCLUDED_LEADS[plan] : 0;
  const returnTo = `${path('/payment-complete')}?${params.toString()}`;
  const card = 'rounded-2xl border border-slate-200 bg-white p-7 shadow-sm';
  const bigBtn =
    'flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl px-5 text-base font-bold transition-colors';

  return (
    <div className="mx-auto max-w-lg px-4 py-14 sm:px-6">
      <Seo title={t('payret.title')} description={t('payret.waitBody')} noindex />

      {/* ---------------- STEP 1: payment received ---------------- */}
      <div className={card}>
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
          <div>
            <h1 className="text-xl font-extrabold text-slate-900">{t('payret.paid')}</h1>
            {plan && (
              <p className="text-sm font-semibold text-slate-500">
                {planName} · {money(PLAN_PRICES[plan] || 0)} · {planCredits} {t('dash.includedLeads')}
              </p>
            )}
          </div>
        </div>

        {/* ---------------- STEP 2: activating / activated ---------------- */}
        {phase === 'waiting' && (
          <div className="mt-6 rounded-xl bg-blue-50 px-4 py-4">
            <p className="flex items-center gap-2 text-sm font-bold text-blue-900">
              <Loader2 className="h-4 w-4 animate-spin" /> {t('payret.activating')}
            </p>
            <p aria-live="polite" className="mt-1.5 text-sm leading-relaxed text-blue-800">
              {t('payret.waitBody')}
            </p>
            {elapsed > 15000 && (
              <p className="mt-2 text-xs font-semibold text-blue-700">{t('payret.stillWorking')}</p>
            )}
          </div>
        )}

        {phase === 'activated' && (
          <div className="mt-6 rounded-xl bg-emerald-50 px-4 py-4">
            <p className="flex items-center gap-2 text-base font-extrabold text-emerald-900">
              <CreditCard className="h-5 w-5" />
              {credits} {credits === 1 ? t('payret.creditReady') : t('payret.creditsReady')}
            </p>
            <p className="mt-1.5 text-sm font-semibold text-emerald-800">{t('payret.sending')}</p>
            <Link to={path('/jobs')} className={`${bigBtn} mt-4 bg-orange-500 text-white hover:bg-orange-600`}>
              {t('payret.browse')} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        )}

        {/* ---- Signed out, account exists -> sign in and come straight back ---- */}
        {phase === 'signin' && (
          <div className="mt-6">
            <p className="text-sm leading-relaxed text-slate-600">{t('payret.signinBody')}</p>
            <Link
              to={`${path('/login')}?next=${encodeURIComponent(returnTo)}${emailParam ? '' : ''}`}
              className={`${bigBtn} mt-4 bg-slate-900 text-white hover:bg-slate-800`}
            >
              <LogIn className="h-5 w-5" /> {t('payret.signin')}
            </Link>
            <p className="mt-3 text-center text-xs font-semibold text-slate-400">{t('payret.thenProjects')}</p>
          </div>
        )}

        {/* ---- Signed out, no account -> secure activation, then continue ---- */}
        {phase === 'signup' && (
          <div className="mt-6">
            <p className="text-sm leading-relaxed text-slate-600">{t('payret.signupBody')}</p>
            <Link
              to={`${path('/register')}?next=${encodeURIComponent(returnTo)}${
                emailParam ? `&email=${encodeURIComponent(emailParam)}` : ''
              }&role=sub`}
              className={`${bigBtn} mt-4 bg-orange-500 text-white hover:bg-orange-600`}
            >
              <UserPlus className="h-5 w-5" /> {t('payret.activateAccount')}
            </Link>
            <p className="mt-3 text-center text-xs font-semibold text-slate-400">{t('payret.thenProjects')}</p>
          </div>
        )}

        {/* ---- Webhook still not here after 90s -> never a dead end ---- */}
        {phase === 'help' && (
          <div className="mt-6 rounded-xl bg-amber-50 px-4 py-4">
            <p className="text-sm font-bold text-amber-900">{t('payret.slowTitle')}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-amber-800">{t('payret.slowBody')}</p>
            <div className="mt-4 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={() => {
                  started.current = Date.now();
                  setElapsed(0);
                  setPhase('waiting');
                }}
                className={`${bigBtn} bg-slate-900 text-white hover:bg-slate-800`}
              >
                <Loader2 className="h-4 w-4" /> {t('payret.checkAgain')}
              </button>
              <a
                href={`mailto:${BRAND.email}?subject=${encodeURIComponent('Payment activation — ' + BRAND.name)}`}
                className={`${bigBtn} border-2 border-amber-600 text-amber-900 hover:bg-amber-100`}
              >
                <Mail className="h-4 w-4" /> {BRAND.email}
              </a>
            </div>
          </div>
        )}

        <p className="mt-6 flex items-start gap-2 border-t border-slate-100 pt-4 text-[11px] leading-relaxed text-slate-400">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t('payret.webhookNote')}
        </p>
      </div>

      {/* Signed-in users always get an escape hatch to their dashboard. */}
      {user && (
        <div className="mt-4 text-center">
          <Link to={path('/dashboard')} className="text-sm font-bold text-slate-500 hover:text-slate-900">
            {t('payret.dashboard')} — {profile?.email || user.email}
          </Link>
        </div>
      )}
    </div>
  );
};

export default PaymentComplete;
